import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';

const PINK = '#E091A8';
const SUGGESTED_TAGS = ['technician', 'supplier', 'manufacturer', 'IT', 'customer', 'internal'];

const trimOrNull = (v) => { const s = (v ?? '').trim(); return s === '' ? null : s; };

const ContactRow = ({ contact, onEdit }) => (
  <button
    type="button"
    onClick={() => onEdit(contact)}
    class="grid w-full grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border-subtle px-4 py-3 text-left last:border-b-0 hover:bg-bg-hover"
  >
    <div class="min-w-0">
      <p class="m-0 truncate font-serif text-[15px] font-medium text-text-primary">{contact.name}</p>
      <p class="m-0 text-[11px] font-medium text-text-tertiary">
        {[contact.role, contact.company].filter(Boolean).join(' · ') || '—'}
        {(contact.phone || contact.email) && (
          <span class="ml-2 font-mono text-text-muted">
            · {contact.phone ?? ''} {contact.email ?? ''}
          </span>
        )}
      </p>
    </div>
    <div class="flex flex-wrap gap-1">
      {(contact.tags ?? []).map((t) => (
        <span key={t} class="rounded-full border border-border-strong bg-bg-base px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.04em] text-text-tertiary">
          {t}
        </span>
      ))}
    </div>
  </button>
);

const ContactModal = ({ open, onClose, onSaved, contact }) => {
  const editing = !!contact;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: contact.name ?? '', company: contact.company ?? '', role: contact.role ?? '',
        phone: contact.phone ?? '', email: contact.email ?? '', notes: contact.notes ?? '',
        tags: contact.tags ?? [],
      } : { name: '', company: '', role: '', phone: '', email: '', notes: '', tags: [] });
      setError(null);
    }
  }, [open, editing, contact]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));
  const toggleTag = (t) => setForm((f) => ({
    ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
  }));

  const save = async () => {
    if (!form.name?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        company: trimOrNull(form.company),
        role: trimOrNull(form.role),
        phone: trimOrNull(form.phone),
        email: trimOrNull(form.email),
        notes: trimOrNull(form.notes),
        tags: form.tags,
      };
      const saved = editing
        ? await api.patch(`/contacts/${contact.id}`, payload)
        : await api.post('/contacts', payload);
      onSaved?.(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      onSaved?.();
    } catch (err) {
      alert(err.message ?? 'Failed');
    }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: PINK }}>{editing ? 'Edit contact' : 'New contact'}</p>
        </div>
        <div class="space-y-3 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Name</p>
            <input autoFocus type="text" value={form.name ?? ''} onInput={set('name')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Company</p>
              <input type="text" value={form.company ?? ''} onInput={set('company')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Role</p>
              <input type="text" value={form.role ?? ''} onInput={set('role')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Phone</p>
              <input type="text" value={form.phone ?? ''} onInput={set('phone')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Email</p>
              <input type="email" value={form.email ?? ''} onInput={set('email')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Tags</p>
            <div class="flex flex-wrap gap-1.5">
              {SUGGESTED_TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                  class={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                    form.tags?.includes(t)
                      ? 'border-brand bg-brand/[0.12] text-brand'
                      : 'border-border-strong text-text-tertiary hover:bg-bg-card'
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Notes</p>
            <textarea rows={3} value={form.notes ?? ''} onInput={set('notes')}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </div>
          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
            </p>
          )}
        </div>
        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {editing && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={busy || !form.name?.trim()}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ContactsPage = () => {
  const [tag, setTag] = useState('all');
  const [search, setSearch] = useState('');
  const qs = useMemo(() => {
    const q = new URLSearchParams();
    if (tag !== 'all') q.set('tag', tag);
    if (search) q.set('search', search);
    const s = q.toString();
    return s ? `?${s}` : '';
  }, [tag, search]);
  const { data: contacts, refetch } = useApi(`/contacts${qs}`);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [editing, setEditing] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const tagsInUse = useMemo(() => {
    const s = new Set();
    (contacts ?? []).forEach((c) => (c.tags ?? []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [contacts]);

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: PINK }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: PINK }}>
              Contacts
            </span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
            Address book
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            Suppliers, technicians, manufacturers. Used across Equipment, Maintenance, and anywhere else a person needs attaching.
          </p>
        </div>
        {isAdmin && <Button variant="primary" onClick={() => setNewOpen(true)}>+ New contact</Button>}
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setTag('all')}
          class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${tag === 'all' ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}>All</button>
        {tagsInUse.map((t) => (
          <button key={t} type="button" onClick={() => setTag(t)}
            class={`rounded-full border px-3 py-1 text-[11px] font-semibold capitalize ${tag === t ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}>{t}</button>
        ))}
        <input type="search" value={search} onInput={(e) => setSearch(e.currentTarget.value)} placeholder="Search…"
          class="ml-auto block w-56 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
      </div>

      {contacts == null && <p class="text-sm text-text-tertiary">Loading…</p>}
      {contacts && contacts.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No contacts.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">Add one with "+ New contact".</p>
        </div>
      )}
      {contacts && contacts.length > 0 && (
        <div class="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
          {contacts.map((c) => <ContactRow key={c.id} contact={c} onEdit={setEditing} />)}
        </div>
      )}

      <ContactModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSaved={() => { setNewOpen(false); refetch(); }}
      />
      <ContactModal
        open={!!editing}
        contact={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refetch(); }}
      />
    </div>
  );
};
